import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TaxReturnFormValues } from "../types/tax-return";
import { useState, useEffect } from "react";

const formSchema = z.object({
  full_name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(10, "Phone number must be at least 10 digits"),
  address: z.string().min(5, "Address must be at least 5 characters"),
  tfn: z.string().length(9, "TFN must be exactly 9 digits"),
  service_type: z.enum(["online", "office"]),
  preferred_office: z.enum(["melbourne", "launceston"]).optional(),
  bsb: z.string().optional(),
  account_number: z.string().optional(),
  account_name: z.string().optional(),
  financial_year: z.string(),
  id: z.string().optional(),
  service_id: z.string().optional(),
});

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function useTaxReturnForm() {
  const [isLoading, setIsLoading] = useState(true);
  const [taxReturnId, setTaxReturnId] = useState<string | null>(null);
  const { toast } = useToast();
  
  const form = useForm<TaxReturnFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      service_type: "online",
      financial_year: new Date().getFullYear().toString(),
    },
  });

  useEffect(() => {
    async function initializeForm() {
      try {
        setIsLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error('No authenticated user found');
        }

        // Get current pricing for TAX service
        const { data: pricing, error: pricingError } = await supabase
          .from('service_pricing')
          .select('*')
          .eq('service_type', 'TAX')
          .is('effective_to', null)
          .order('effective_from', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (pricingError) {
          console.error('Pricing error:', pricingError);
          throw new Error('Failed to fetch service pricing');
        }
        if (!pricing) {
          console.error('No pricing found');
          throw new Error('No pricing configuration found for TAX service');
        }

        // Fetch profile data
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (!profile) {
          console.error('No profile found');
          throw new Error('User profile not found');
        }

        // Generate service number using edge function
        const { data: serviceNumberData, error: serviceNumberError } = await supabase.functions
          .invoke('generate-service-number');

        if (serviceNumberError) {
          console.error('Service number generation error:', serviceNumberError);
          throw new Error('Failed to generate service number');
        }

        if (!serviceNumberData?.serviceNumber) {
          throw new Error('No service number received from edge function');
        }

        // Create service record
        const { data: service, error: serviceError } = await supabase
          .from('services')
          .insert({
            service_type: 'TAX',
            client_id: user.id,
            status: 'draft',
            amount: pricing.base_price,
            gst_amount: pricing.base_price * 0.1,
            service_number: serviceNumberData.serviceNumber
          })
          .select()
          .single();

        if (serviceError) {
          console.error('Service creation error:', serviceError);
          throw new Error('Failed to create service record');
        }

        // Create tax return request
        const { data: taxReturn, error: taxReturnError } = await supabase
          .from('tax_return_requests')
          .insert({
            user_id: user.id,
            financial_year: new Date().getFullYear().toString(),
            service_type: 'online',
            status: 'draft',
            client_number: profile.client_number
          })
          .select()
          .single();

        if (taxReturnError) {
          console.error('Tax return error:', taxReturnError);
          throw taxReturnError;
        }

        setTaxReturnId(taxReturn.id);

        // Pre-fill form with profile data
        form.reset({
          ...form.getValues(),
          full_name: profile.full_name || '',
          email: profile.email || '',
          phone: profile.phone?.replace(/^\+61|^61/, '0') || '',
          address: profile.address || '',
          tfn: profile.tfn || '',
          id: taxReturn.id,
          service_id: service.id,
        });

      } catch (error: any) {
        console.error('Error initializing form:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to initialize tax return request. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }

    initializeForm();
  }, [form, toast]);

  return { form, isLoading, taxReturnId };
}